import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as d3 from 'd3';
import { ChartProps } from './types';
import { getSeriesColor } from './color-palette';
import { ChartPatternDefs } from './ChartPatternDefs';
import { ChartTooltip, TooltipData } from './ChartTooltip';

const MARGIN = { top: 20, right: 20, bottom: 60, left: 60 };
const WIDTH = 600;
const HEIGHT = 400;

/**
 * Histogram chart for cycle time / lead time distribution.
 * Data format: dimensionValue = bucket label (e.g., "0-1", "1-2"), measureValue = card count.
 */
export function HistogramChart({ data, dimensionLabel, measureLabel, onSegmentClick }: ChartProps): React.ReactElement {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<TooltipData | null>(null);

  const handleBarInteraction = useCallback(
    (dimensionValue: string, measureValue: number, event: React.MouseEvent | React.FocusEvent) => {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const target = event.currentTarget as HTMLElement;
      const targetRect = target.getBoundingClientRect();
      setTooltip({ dimensionValue, measureValue, dimensionLabel, measureLabel, x: targetRect.left - rect.left + targetRect.width / 2, y: targetRect.top - rect.top });
    },
    [dimensionLabel, measureLabel],
  );

  const hideTooltip = useCallback(() => setTooltip(null), []);

  useEffect(() => {
    if (!svgRef.current || data.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('g.chart-content').remove();

    const innerWidth = WIDTH - MARGIN.left - MARGIN.right;
    const innerHeight = HEIGHT - MARGIN.top - MARGIN.bottom;

    const g = svg.append('g').attr('class', 'chart-content').attr('transform', `translate(${MARGIN.left},${MARGIN.top})`);

    const xScale = d3.scaleBand().domain(data.map((d) => d.dimensionValue)).range([0, innerWidth]).padding(0.05);
    const yScale = d3.scaleLinear().domain([0, d3.max(data, (d) => d.measureValue) ?? 0]).nice().range([innerHeight, 0]);

    // Gridlines
    g.append('g').call(d3.axisLeft(yScale).tickSize(-innerWidth).tickFormat(() => '')).selectAll('line').attr('stroke', '#e5e7eb').attr('stroke-dasharray', '2,2');
    g.select('.domain').remove();

    // Axes
    g.append('g').attr('transform', `translate(0,${innerHeight})`).call(d3.axisBottom(xScale)).selectAll('text').attr('font-size', '11px');
    g.append('g').call(d3.axisLeft(yScale)).attr('font-size', '12px');

    // Bars (histogram style - no gap between bars)
    const color = '#2563eb';
    data.forEach((d) => {
      g.append('rect')
        .attr('x', xScale(d.dimensionValue) ?? 0)
        .attr('y', yScale(d.measureValue))
        .attr('width', xScale.bandwidth())
        .attr('height', innerHeight - yScale(d.measureValue))
        .attr('fill', color)
        .attr('stroke', 'white')
        .attr('stroke-width', 0.5);
    });

    // Axis labels
    g.append('text').attr('x', innerWidth / 2).attr('y', innerHeight + MARGIN.bottom - 5).attr('text-anchor', 'middle').attr('font-size', '13px').attr('fill', 'currentColor').text(dimensionLabel || 'Days');
    g.append('text').attr('transform', 'rotate(-90)').attr('x', -innerHeight / 2).attr('y', -MARGIN.left + 15).attr('text-anchor', 'middle').attr('font-size', '13px').attr('fill', 'currentColor').text(measureLabel || 'Number of Cards');
  }, [data, dimensionLabel, measureLabel]);

  if (data.length === 0) {
    return <div className="flex h-64 items-center justify-center text-muted-foreground" role="status">No completed cards in this time period</div>;
  }

  const innerWidth = WIDTH - MARGIN.left - MARGIN.right;
  const innerHeight = HEIGHT - MARGIN.top - MARGIN.bottom;
  const xScale = d3.scaleBand().domain(data.map((d) => d.dimensionValue)).range([0, innerWidth]).padding(0.05);
  const yScale = d3.scaleLinear().domain([0, d3.max(data, (d) => d.measureValue) ?? 0]).nice().range([innerHeight, 0]);

  return (
    <div ref={containerRef} className="relative" role="img" aria-label={`Histogram: ${dimensionLabel} by ${measureLabel}`}>
      <svg ref={svgRef} viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className="h-auto w-full max-w-[600px]" aria-hidden="true"><ChartPatternDefs /></svg>
      <div className="absolute inset-0" aria-label="Chart data points" role="list">
        {data.map((d) => {
          const x = (MARGIN.left + (xScale(d.dimensionValue) ?? 0)) / WIDTH * 100;
          const y = (MARGIN.top + yScale(d.measureValue)) / HEIGHT * 100;
          const bw = xScale.bandwidth() / WIDTH * 100;
          const bh = (innerHeight - yScale(d.measureValue)) / HEIGHT * 100;
          return (
            <button key={d.dimensionValue} type="button" role="listitem" className="absolute border-0 bg-transparent p-0 outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2" style={{ left: `${x}%`, top: `${y}%`, width: `${bw}%`, height: `${bh}%` }} tabIndex={0} aria-label={`${dimensionLabel}: ${d.dimensionValue} days, ${measureLabel}: ${d.measureValue} cards`} onMouseEnter={(e) => handleBarInteraction(d.dimensionValue, d.measureValue, e)} onFocus={(e) => handleBarInteraction(d.dimensionValue, d.measureValue, e)} onMouseLeave={hideTooltip} onBlur={hideTooltip} onClick={() => onSegmentClick?.(d.dimensionValue)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSegmentClick?.(d.dimensionValue); } }} />
          );
        })}
      </div>
      <ChartTooltip tooltip={tooltip} />
    </div>
  );
}
